import { useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport, type UIMessage } from 'ai';
import { Button } from '@ai-tutor/ui';
import {
  ArrowDown,
  Loader2,
  MessageSquare,
  RotateCcw,
  Send,
  Square,
} from 'lucide-react';
import { notify } from '@/lib/notify';
import { track } from '@/config/observability';
import { supabase } from '@/config/supabase';
import { apiUrl, lessonApi } from '@/lib/api-client';
import { subscribeChatPrefill } from '@/lib/chat-prefill';
import {
  budgetAwareFetch,
  isBudgetExceededError,
} from '@/lib/api/budget-fetch';
import { ChatAvatar, MessageBubble } from './MessageBubble';
import { extractMessage, historyToUIMessages } from './chatMessageParts';
import { useCreateNote } from '@/hooks/lesson/useNotes';
import { ErrorState } from '@/components/core/common/boundaries/ErrorState';

interface ChatInterfaceProps {
  lessonId?: string;
  className?: string;
  /** The conversation thread the user is currently viewing. Owned by
   *  `DoubtsExplorePanel` (the chat panel) so the conversation
   *  selector + new-chat button can live in the panel's heading. Null
   *  while the panel's bootstrap effect is still resolving. */
  activeConversationId: string | null;
  /** Fires when the AI SDK reports a stream finished. Lets the parent
   *  refresh conversation summaries (title from first message,
   *  message_count, updated_at) so the dropdown stays in sync. */
  onMessageSent?: () => void;
}

const suggestedPrompts = [
  'Explain this concept in simpler terms',
  'Give me a real-world example',
  'What are the key takeaways?',
  'How does this relate to other topics?',
];

/** The thread without its last message, when that message is the user's. */
function dropTrailingUserTurn(messages: UIMessage[]): UIMessage[] {
  const last = messages[messages.length - 1];
  return last?.role === 'user' ? messages.slice(0, -1) : messages;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : {};
}

/** Longest message the backend accepts (`MAX_QUESTION_CHARS`). */
const MAX_MESSAGE_CHARS = 4000;

export function ChatInterface({
  lessonId,
  className,
  activeConversationId,
  onMessageSent,
}: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  // The history request failed. It used to fall through to an empty message
  // list, which renders "Start a conversation" - reading as if the user's
  // past messages were gone. A failure is its own state, with a retry.
  const [historyFailed, setHistoryFailed] = useState(false);
  const [historyAttempt, setHistoryAttempt] = useState(0);
  // True when the user is scrolled away from the bottom - pauses auto-
  // scroll and reveals a floating "Jump to latest" chip.
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // The last message sent, so a turn refused at the usage limit can be put
  // back in the input instead of lost.
  const lastSentRef = useRef('');

  // Cross-component prefill: lets the text-mode selection popover (or any
  // future component) request that some text be staged in the chat input.
  useEffect(() => {
    return subscribeChatPrefill(({ text, focus }) => {
      setInput(text);
      if (focus) {
        // Defer focus so the input has the new value before cursor lands.
        requestAnimationFrame(() => {
          inputRef.current?.focus();
          const len = inputRef.current?.value.length ?? 0;
          inputRef.current?.setSelectionRange(len, len);
        });
      }
    });
  }, []);

  // ── AI SDK v5 useChat ─────────────────────────────────────────────────
  // Pass `id={activeConversationId}` so the SDK rebuilds its internal
  // chat instance when the user switches threads - that's how we get
  // a fresh transport pointed at the new `/conversations/{id}/chat`
  // URL without leaking state from the previous conversation.
  // Must be an ABSOLUTE URL (api.xonera.xyz), not a bare `/api/v1/...` path.
  // A relative path resolves against the frontend origin (app.xonera.xyz),
  // whose Vercel SPA rewrite serves static index.html and 405s the POST - the
  // chat request never reaches the backend. All other calls go through
  // api-client, which already prefixes the API base; mirror that here.
  const chatApiUrl =
    lessonId && activeConversationId
      ? apiUrl(
          `/lessons/${lessonId}/conversations/${activeConversationId}/chat`
        )
      : null;

  const {
    messages,
    setMessages,
    sendMessage,
    regenerate,
    stop,
    status,
    error,
  } = useChat({
    id: activeConversationId ?? 'inactive',
    transport: new DefaultChatTransport({
      api: chatApiUrl ?? apiUrl('/chat'),
      headers: getAuthHeaders,
      // The transport bypasses api-client, so this is what turns a 402 into
      // the app-wide limit modal (and a typed error instead of raw JSON).
      fetch: budgetAwareFetch,
      // Send only the newest user message. The backend reads nothing else -
      // history comes from the stored conversation - and resending the whole
      // thread every turn grew each request with the conversation (the
      // backend caps `messages`).
      prepareSendMessagesRequest: ({
        id,
        messages,
        trigger,
        messageId,
        headers,
      }) => {
        const lastUser = [...messages].reverse().find(m => m.role === 'user');
        return {
          headers,
          body: {
            id,
            trigger,
            messageId,
            messages: lastUser ? [lastUser] : [],
          },
        };
      },
    }),
    // ~20fps cap on React re-renders during fast token bursts. The text
    // still streams in at the LLM's rate; we just batch state updates so
    // markdown re-rendering doesn't pin the main thread.
    experimental_throttle: 50,
    // Notify the parent so it can refresh the conversation summaries
    // (title/timestamp/message_count) for its dropdown without a page
    // reload. Parent owns the list, so we just ping.
    onFinish: () => {
      onMessageSent?.();
    },
    // No toast: the error banner under the messages already says it, with
    // a Retry. Reporting both put the same message on screen twice.
    //
    // At the usage limit the modal says it instead, and the turn never
    // happened: drop its user bubble and put the text back so the user can
    // send it once the limit resets.
    onError: err => {
      if (!isBudgetExceededError(err)) return;
      const sent = lastSentRef.current;
      setMessages(current => dropTrailingUserTurn(current));
      setInput(current => current || sent);
    },
  });
  const chatError = isBudgetExceededError(error) ? undefined : error;

  // ── Load message history when active conversation changes ────────────
  // We hydrate `useChat`'s internal state via `setMessages` so it owns
  // the single source of truth. No parallel `historyMessages` state to
  // merge during render.
  useEffect(() => {
    if (!lessonId || !activeConversationId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    setIsHistoryLoading(true);
    setHistoryFailed(false);
    (async () => {
      try {
        const data = await lessonApi.getConversation(
          lessonId,
          activeConversationId
        );
        if (cancelled) return;
        setMessages(historyToUIMessages(data.messages));
      } catch {
        if (!cancelled) {
          setMessages([]);
          setHistoryFailed(true);
        }
      } finally {
        if (!cancelled) setIsHistoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lessonId, activeConversationId, setMessages, historyAttempt]);

  const isLoading = status === 'streaming' || status === 'submitted';
  // Show the 3-dot "thinking" indicator only during the submitted phase -
  // i.e. while we're waiting for the LLM's first token. Once streaming
  // begins (`status === 'streaming'`), the assistant bubble itself is
  // filling in with text so the typing dots underneath would be
  // redundant and feel laggy.
  const showTypingIndicator = status === 'submitted';

  // ── Auto-scroll ────────────────────────────────────────────────────────
  // Only auto-scroll when the user is already at or near the bottom.
  // Otherwise they're reading older messages and a yank to the bottom on
  // every stream chunk would be jarring; the "Jump to latest" chip below
  // gives them an explicit way back down.
  useEffect(() => {
    if (isScrolledUp) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isScrolledUp]);

  // Land on the latest message when a conversation's history arrives. The
  // effect above cannot do it: the history is set while the loading spinner is
  // still up, so it runs with no list mounted - and when the spinner clears,
  // `messages` has not changed, so it never runs again. A long thread opened
  // scrolled to its FIRST message. Instant, not smooth: this is where the
  // thread opens, not a movement the user asked for.
  useEffect(() => {
    if (isHistoryLoading) return;
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [isHistoryLoading, activeConversationId]);

  // Track scroll position so we can suspend auto-scroll + reveal the
  // jump-to-latest chip when the user reads back through history. 80px
  // tolerance keeps the chip from flickering during a streaming update.
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setIsScrolledUp(distanceFromBottom > 80);
  };

  const scrollToLatest = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading || !activeConversationId) return;
    const text = input;
    lastSentRef.current = text;
    setInput('');
    if (lessonId) {
      track('tutor_message_sent', {
        lesson_id: lessonId,
        message_length: text.length,
      });
    }
    await sendMessage({ text });
  };

  // Regenerate the last assistant turn. The SDK strips the trailing
  // assistant message from local state and POSTs with
  // `trigger: "regenerate-message"`; the backend replaces the prior
  // assistant reply in MongoDB and streams a fresh one. Used both by
  // the per-bubble Regenerate button and by the error banner retry.
  const handleRegenerate = async () => {
    if (isLoading || !activeConversationId) return;
    await regenerate();
  };

  // True when there's at least one assistant turn we could regenerate.
  // The button only makes sense after a completed exchange.
  const canRegenerate = useMemo(() => {
    if (isLoading) return false;
    return messages.some(m => m.role === 'assistant');
  }, [messages, isLoading]);

  // Save-to-notes - shared mutation so every assistant bubble in this
  // chat session goes through one hook instance. Title comes from the
  // preceding user question (truncated to keep the note title
  // single-line); body is the assistant's full markdown answer.
  const createNote = useCreateNote(lessonId ?? '', 'chat');

  const handleSaveToNote = async (question: string, answer: string) => {
    if (!lessonId) return;
    const trimmedQ = question.trim();
    const title =
      trimmedQ.length > 80
        ? trimmedQ.slice(0, 80).trimEnd() + '…'
        : trimmedQ || 'Chat answer';
    try {
      await createNote.mutateAsync({ title, content: answer });
      notify.success('Saved to notes');
    } catch (err: any) {
      notify.error(err, "Couldn't save to notes");
    }
  };

  const allEmpty = messages.length === 0;

  return (
    <div className={`relative flex h-full flex-col ${className ?? ''}`}>
      {/* Messages Area - the conversation selector lives in the parent
          chat panel's heading now (DoubtsExplorePanel), not here. */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto p-4'
      >
        {isHistoryLoading && (
          <div className='flex h-full items-center justify-center'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        )}
        {!isHistoryLoading && historyFailed && (
          <ErrorState
            variant='panel'
            title="Couldn't load this conversation"
            description='Your messages are safe. Check your connection and try again.'
            primaryAction={{
              label: 'Try again',
              onClick: () => setHistoryAttempt(n => n + 1),
            }}
          />
        )}
        {!isHistoryLoading && !historyFailed && allEmpty && (
          <div className='flex h-full flex-col items-center justify-center py-8'>
            <div className='mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 dark:bg-muted'>
              <MessageSquare className='h-8 w-8 text-brand-500/80 dark:text-primary' />
            </div>
            <h3 className='mb-2 text-lg font-medium text-foreground'>
              Start a conversation
            </h3>
            <p className='mb-6 max-w-xs text-center text-sm text-muted-foreground'>
              Ask questions about your lesson or explore concepts
            </p>

            <div className='w-full max-w-md space-y-2'>
              <p className='mb-2 text-xs text-muted-foreground'>Try asking:</p>
              {suggestedPrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => setInput(prompt)}
                  className='min-h-11 w-full rounded-lg border border-brand-100 bg-brand-50/40 px-3 py-2 text-left text-sm text-foreground transition-colors duration-200 hover:border-brand-200 hover:bg-brand-50 dark:border-primary/20 dark:bg-primary/5 dark:hover:border-primary dark:hover:bg-primary/15 sm:min-h-0'
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}
        {!isHistoryLoading && !allEmpty && (
          // `role='list'` + a name. The bubbles are `role='listitem'`, which
          // axe rejects without a list parent (`aria-required-parent`) — the
          // violation the ChatInterface story used to disable the rule for.
          // A listitem with no list is not a smaller problem than no roles at
          // all: assistive tech cannot report position ("message 3 of 7"),
          // which is the only reason to claim the role in the first place.
          <div className='space-y-4' role='list' aria-label='Conversation'>
            {/* Unified render path: `messages` holds both hydrated
                history (loaded via `setMessages`) and freshly streamed
                turns. Citation chips work in both cases because
                `data-citation` parts are produced for history during
                hydration and streamed in by the backend for new turns. */}
            {messages.map((message, idx) => {
              const { text, sources, images, visuals } =
                extractMessage(message);
              const prev = idx > 0 ? messages[idx - 1] : undefined;
              const previousUserText =
                message.role === 'assistant' && prev?.role === 'user'
                  ? extractMessage(prev).text
                  : '';
              const isLastAssistant =
                message.role === 'assistant' && idx === messages.length - 1;
              return (
                <MessageBubble
                  key={message.id}
                  role={message.role as 'user' | 'assistant'}
                  text={text}
                  sources={sources}
                  images={images}
                  visuals={visuals}
                  previousUserText={previousUserText}
                  onSaveToNote={handleSaveToNote}
                  onRegenerate={
                    isLastAssistant && canRegenerate
                      ? handleRegenerate
                      : undefined
                  }
                />
              );
            })}

            {/* Typing indicator - only while waiting for the first token.
                aria-live="polite" lets screen readers announce that the
                assistant is responding. */}
            {showTypingIndicator && (
              <div
                className='flex gap-2'
                role='status'
                aria-live='polite'
                aria-label='Assistant is responding'
              >
                <ChatAvatar variant='assistant' />
                <div className='rounded-2xl rounded-bl-md border border-border bg-muted px-3.5 py-2.5'>
                  <div className='flex gap-1.5'>
                    <span className='h-2 w-2 animate-bounce rounded-full bg-brand-400/80 [animation-delay:-0.4s] [animation-duration:1.2s] dark:bg-primary/80' />
                    <span className='h-2 w-2 animate-bounce rounded-full bg-brand-400/80 [animation-delay:-0.2s] [animation-duration:1.2s] dark:bg-primary/80' />
                    <span className='h-2 w-2 animate-bounce rounded-full bg-brand-400/80 [animation-duration:1.2s] dark:bg-primary/80' />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        {chatError && (
          <div className='mt-2 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/40 dark:bg-rose-900/20'>
            <p className='flex-1 text-sm text-rose-700 dark:text-rose-300'>
              {chatError.message}
            </p>
            {canRegenerate && (
              <button
                type='button'
                onClick={handleRegenerate}
                disabled={isLoading}
                className='inline-flex flex-shrink-0 items-center gap-1 rounded-md border border-rose-200 bg-card px-2 py-1 text-xs font-medium text-rose-700 transition-colors hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900/50 dark:bg-rose-900/30 dark:text-rose-300 dark:hover:bg-rose-900/50'
              >
                <RotateCcw className='h-3 w-3' />
                Retry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Jump-to-latest chip - appears when the user has scrolled away
          from the bottom. Clicking jumps to the newest message and
          re-enables auto-scroll. */}
      {isScrolledUp && (
        <button
          type='button'
          onClick={scrollToLatest}
          aria-label='Jump to latest message'
          className='absolute bottom-24 left-1/2 z-10 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-brand-200 bg-card px-3 py-1.5 text-xs font-medium text-brand-700 shadow-md transition-all hover:bg-brand-50 dark:border-primary/40 dark:text-primary dark:hover:bg-primary/20'
        >
          <ArrowDown className='h-3.5 w-3.5' />
          Jump to latest
        </button>
      )}

      {/* Input Area */}
      <div className='p-3'>
        <form onSubmit={handleSubmit}>
          <div className='relative rounded-lg border border-border/60 bg-gray-50/80 transition-colors duration-200 focus-within:border-brand-400 dark:bg-muted dark:focus-within:border-primary'>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              // Mirrors the backend's MAX_QUESTION_CHARS; it refuses longer.
              maxLength={MAX_MESSAGE_CHARS}
              placeholder={
                lessonId ? 'Ask anything...' : 'Open a lesson to chat'
              }
              disabled={isLoading || !lessonId || historyFailed}
              className='max-h-[120px] min-h-[60px] w-full resize-none border-0 bg-transparent px-4 py-3 pr-12 text-base text-foreground placeholder-gray-500 shadow-none outline-0 ring-0 focus:shadow-none focus:outline-0 focus:ring-0 disabled:opacity-50 dark:placeholder-gray-400 sm:text-sm'
              style={{ boxShadow: 'none' }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />

            {input.length > MAX_MESSAGE_CHARS * 0.85 && (
              <span
                aria-live='polite'
                className='absolute bottom-3 right-12 font-mono text-xs tabular-nums text-muted-foreground'
              >
                {input.length.toLocaleString()}/
                {MAX_MESSAGE_CHARS.toLocaleString()}
              </span>
            )}

            {/* Send / Stop button - toggles while streaming so the user
                can abort mid-response. The Stop click triggers the SDK's
                `stop()`, which aborts the fetch; the backend polls
                `request.is_disconnected()` and persists what was streamed
                so far with `truncated=true`. */}
            {isLoading ? (
              <Button
                type='button'
                aria-label='Stop generating'
                onClick={() => stop()}
                size='sm'
                className='absolute bottom-1.5 right-1.5 h-11 w-11 rounded-full p-0 sm:bottom-3 sm:right-3 sm:h-7 sm:w-7'
              >
                <Square className='h-3 w-3 fill-white text-white dark:fill-gray-900' />
              </Button>
            ) : (
              <Button
                type='submit'
                aria-label='Send'
                disabled={!input.trim() || !lessonId || historyFailed}
                size='sm'
                className='absolute bottom-1.5 right-1.5 h-11 w-11 rounded-full p-0 disabled:cursor-not-allowed disabled:opacity-30 sm:bottom-3 sm:right-3 sm:h-7 sm:w-7'
              >
                <Send className='h-3.5 w-3.5 text-white dark:text-primary-foreground' />
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────
